/**
 * Shared hook for loading and filtering a school directory on level/exam pages.
 *
 * What: Loads the school list from a prerendered <script> tag (fast, no fetch) and
 *       falls back to a network fetch for SPA navigations. Applies an optional filter.
 * Why: Every level and exam page needs schools but the prerender data format and
 *      hydration contract is the same — centralising avoids N near-identical copies.
 * When: Update when the prerender script ID scheme or fetch API changes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import type { SchoolRecord } from '@shared/schools/schoolData.js';
import { parseSchoolRecordList } from '@shared/schools/schoolRecordSchema.js';
import {
  loadPrimarySchools,
  loadSecondarySchools,
} from '@/services/schoolDataApi.js';

type SchoolType = 'primary' | 'secondary';

/** Script IDs used by the prerender pipeline to embed school lists. */
const PRERENDER_SCRIPT_IDS: Record<SchoolType, string> = {
  primary: '__hub-school-list-primary__',
  secondary: '__hub-school-list-secondary__',
};

function readPrerenderSchools(
  schoolType: SchoolType
): readonly SchoolRecord[] | null {
  if (typeof document === 'undefined') return null;
  try {
    const el = document.getElementById(PRERENDER_SCRIPT_IDS[schoolType]);
    if (!el) return null;
    return parseSchoolRecordList(JSON.parse(el.textContent ?? ''));
  } catch {
    return null;
  }
}

interface UseSchoolDirectoryOptions {
  schoolType: SchoolType;
  /** Optional filter applied after loading. Prerendered and fetched data are both filtered. */
  filter?: (schools: readonly SchoolRecord[]) => readonly SchoolRecord[];
}

interface UseSchoolDirectoryResult {
  schools: readonly SchoolRecord[];
  isLoading: boolean;
  hasLoadError: boolean;
}

export function useSchoolDirectory({
  schoolType,
  filter,
}: UseSchoolDirectoryOptions): UseSchoolDirectoryResult {
  const prerenderData = readPrerenderSchools(schoolType);

  const [rawSchools, setRawSchools] = useState<readonly SchoolRecord[]>(
    () => prerenderData ?? []
  );
  const [isLoading, setIsLoading] = useState(prerenderData === null);
  const [hasLoadError, setHasLoadError] = useState(false);

  // Skip the initial fetch when state was seeded from prerendered data.
  // Cleared after the first effect run so SPA navigations still fetch normally.
  const skipInitialFetchRef = useRef(prerenderData !== null);

  useEffect(() => {
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }

    let isActive = true;
    const loadFn =
      schoolType === 'primary' ? loadPrimarySchools : loadSecondarySchools;

    loadFn()
      .then(records => {
        if (!isActive) return;
        setRawSchools(records);
        setHasLoadError(false);
        setIsLoading(false);
      })
      .catch(error => {
        if (!isActive) return;
        console.error(`Failed to load ${schoolType} school directory`, error);
        setRawSchools([]);
        setHasLoadError(true);
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const schools = useMemo(
    () => (filter ? filter(rawSchools) : rawSchools),
    [filter, rawSchools]
  );

  return { schools, isLoading, hasLoadError };
}
